import { useEffect, useMemo, useRef, useState } from "react";

import {
  DataGridPro,
  gridDensityValueSelector,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { COLORS } from "@stacklet/ui";

import { useStickyState } from "app/hooks";

import { useColumnSetup } from "./hooks";
import { LoadingOverlay as CustomLoadingOverlay } from "./LoadingOverlay";
import { NoRowsOverlay as CustomNoRowsOverlay } from "./NoRowsOverlay";
import { CustomFooterComponent as CustomFooter } from "./XGridCustomFooter";
import { XGridCustomToolbar as CustomToolbar } from "./XGridCustomToolbar";

import type {
  DataGridProProps,
  GridAutosizeOptions,
  GridCallbackDetails,
  GridColumnHeaderParams,
  GridColumnResizeParams,
  GridDensity,
  GridRowSelectionModel,
} from "@mui/x-data-grid-pro";
import type { GridInitialStatePro } from "@mui/x-data-grid-pro/models/gridStatePro";

const DEFAULT_GRID_AUTOSIZE_OPTIONS = {
  includeHeaders: true,
  includeOutliers: true,
  expand: true,
};

const TEST_RUN = import.meta.env.VITEST_WORKER_ID !== undefined;

// Static Tailwind classes for the DataGrid. CSS custom properties (--xgrid-*)
// are injected via `style` so they can reference runtime colour tokens.
const BASE_GRID_CLASSES = [
  "mt-1 bg-transparent text-[14px]",
  "[&.MuiDataGrid-root]:border-none",
  "[&_.MuiDataGrid-columnHeaders]:shadow-[inset_0_-1px_0_var(--xgrid-row-border-color)]",
  "[&_.MuiDataGrid-cellCheckbox]:min-w-12 [&_.MuiDataGrid-cellCheckbox]:max-w-12 [&_.MuiDataGrid-cellCheckbox]:justify-center [&_.MuiDataGrid-cellCheckbox]:px-0",
  "[&_.MuiDataGrid-columnHeaderCheckbox]:min-w-12 [&_.MuiDataGrid-columnHeaderCheckbox]:max-w-12 [&_.MuiDataGrid-columnHeaderCheckbox]:justify-center [&_.MuiDataGrid-columnHeaderCheckbox]:px-0",
  "[&_.MuiDataGrid-columnHeaderCheckbox_.MuiDataGrid-columnHeaderTitleContainer]:w-full [&_.MuiDataGrid-columnHeaderCheckbox_.MuiDataGrid-columnHeaderTitleContainer]:justify-center [&_.MuiDataGrid-columnHeaderCheckbox_.MuiDataGrid-columnHeaderTitleContainer]:px-0",
  "[&_.MuiDataGrid-cellCheckbox_.MuiCheckbox-root]:m-0 [&_.MuiDataGrid-columnHeaderCheckbox_.MuiCheckbox-root]:m-0",
  "[&_.MuiDataGrid-row.even]:!bg-data-grid-row-even [&_.MuiDataGrid-row.odd]:!bg-data-grid-row-odd",
  "[&_.MuiDataGrid-row.even:hover]:!bg-data-grid-row-hover [&_.MuiDataGrid-row.even.Mui-hovered]:!bg-data-grid-row-hover",
  "[&_.MuiDataGrid-row.odd:hover]:!bg-data-grid-row-hover [&_.MuiDataGrid-row.odd.Mui-hovered]:!bg-data-grid-row-hover",
  "[&_::-webkit-scrollbar]:h-[7px] [&_::-webkit-scrollbar]:w-[7px] [&_::-webkit-scrollbar]:appearance-none",
  "[&_::-webkit-scrollbar-thumb]:rounded-[4px] [&_::-webkit-scrollbar-thumb]:bg-[rgba(0,0,0,0.5)] [&_::-webkit-scrollbar-thumb]:shadow-[0_0_1px_rgba(255,255,255,0.5)]",
  "[&_.MuiButton-textPrimary]:text-[var(--xgrid-toolbar-button-color)]",
  "[&_.MuiDataGrid-cell:focus]:outline-none [&_.MuiDataGrid-cell:focus-within]:outline-none",
  "[&_.MuiDataGrid-columnHeader]:text-label-medium [&_.MuiDataGrid-columnHeader]:text-text-primary",
  "[&_.XGridTable-clickableRow.MuiDataGrid-row:hover]:cursor-pointer [&_.XGridTable-clickableRow.MuiDataGrid-row:hover]:shadow-[inset_5px_0_#0c5688]",
  "[&_.MuiDataGrid-columnHeaderTitleContainer:hover]:cursor-pointer",
  "[&_.MuiDataGrid-columnHeaderTitle]:font-normal",
  // Autosizing measures `cell.firstElementChild.getBoundingClientRect().width`,
  // but cell renderers (TableLink, TruncatedText, …) wrap content in
  // w-full/flex-1 truncating containers, so the measured "content width" is
  // just the column's current width. While MUI's transient
  // `MuiDataGrid-autosizing` class is applied, let cell content take its
  // natural width (capped, so a single long value can't blow up the column).
  "[&.MuiDataGrid-autosizing_.MuiDataGrid-cell_*]:!w-max",
  "[&.MuiDataGrid-autosizing_.MuiDataGrid-cell_*]:!max-w-[640px]",
  "[&.MuiDataGrid-autosizing_.MuiDataGrid-cell_*]:!flex-none",
].join(" ");

interface CustomProps {
  tableId: string;
  autoHeightMax?: number;
  columnBuffer?: number;
  onColumnHeaderClick?: (params: GridColumnHeaderParams) => void;
  handleExport?: () => void;
  hasToolbar?: boolean;
  customHeight?: number | string;
  exportInProgress?: boolean;
  customInline?: React.ReactNode;
  enableFilterButton?: boolean;
  /** Hides the density selector button in the toolbar when true */
  hideDensityButton?: boolean;
  /** Density used the first time this table renders (before any user override) */
  defaultDensity?: GridDensity;
  /** External apiRef. When provided, used instead of the internally created one. */
  apiRef?: ReturnType<typeof useGridApiRef>;
  /**
   * Fields to hold at the width their column definition declares, exempt from
   * autosizing. Autosize measures rendered content and then shares any leftover
   * viewport width across the columns it sized, so a column of fixed-size
   * content — an icon button, a status dot — otherwise ends up a different
   * width per table and per viewport. Same reasoning as the checkbox column,
   * which is exempt already.
   */
  fixedWidthFields?: string[];
}

type Props = DataGridProProps & CustomProps;

export function XGridTable({
  autoHeightMax,
  customHeight,
  disableVirtualization,
  handleExport,
  hasToolbar = true,
  exportInProgress,
  initialState,
  loading,
  onColumnHeaderClick,
  tableId,
  rowCount,
  rows,
  customInline,
  enableFilterButton = false,
  hideDensityButton = false,
  defaultDensity = "standard",
  fixedWidthFields,
  checkboxSelection,
  rowSelectionModel: externalRowSelectionModel,
  onRowSelectionModelChange: externalOnRowSelectionModelChange,
  columns: rawColumns,
  rowHeight = 40,
  slotProps: slotPropsFromParent,
  className: parentDataGridClassName,
  style: parentDataGridStyle,
  getRowClassName,
  apiRef: externalApiRef,
  ...rest
}: Props) {
  const [tableState, setTableState] = useStickyState<GridInitialStatePro>(
    {},
    `table-state-${tableId}`,
  );
  // Density isn't part of DataGridPro's initialState/exportState (MUI doesn't
  // include it), so it needs its own sticky key rather than living in tableState.
  const [tableDensity, setTableDensity] = useStickyState<GridDensity>(
    defaultDensity,
    `table-density-${tableId}`,
  );
  const [internalRowSelectionModel, setInternalRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  const isControlled = externalRowSelectionModel !== undefined;
  const rowSelectionModel = isControlled
    ? externalRowSelectionModel
    : internalRowSelectionModel;

  const handleRowSelectionModelChange = (
    newModel: GridRowSelectionModel,
    details: GridCallbackDetails,
  ) => {
    if (!isControlled) {
      setInternalRowSelectionModel(newModel);
    }
    externalOnRowSelectionModelChange?.(newModel, details);
  };

  const [autosizeOptions, setAutosizeOptions] = useState<GridAutosizeOptions>(
    DEFAULT_GRID_AUTOSIZE_OPTIONS,
  );
  const hasAutosizedRef = useRef(false);

  const internalApiRef = useGridApiRef();
  const apiRef = externalApiRef ?? internalApiRef;

  const maxAutoHeightRows = autoHeightMax || 30;
  const totalRows = rowCount || rows.length;

  const userResizedWidthsRef = useRef<Record<string, number>>({});

  const setupColumns = useColumnSetup(rawColumns, totalRows);
  // When the `columns` prop identity changes (e.g. crossing the clientside
  // pagination threshold after a search), DataGridPro re-applies each colDef's
  // own dimensions, dropping widths the user set by dragging. Re-inject the
  // user-resized widths so they survive. Reading the ref inside the memo is
  // intentional: widths only need to be re-applied when the identity changes.
  const columns = useMemo(
    () =>
      setupColumns.map((column) => {
        const width = userResizedWidthsRef.current[column.field];
        return width === undefined
          ? column
          : { ...column, width, flex: undefined };
      }),
    [setupColumns],
  );
  const autosizeEligibleColumns = useMemo(
    () =>
      (autosizeOptions.columns && autosizeOptions.columns.length
        ? autosizeOptions.columns
        : columns.map((column) => column.field)
      ).filter(
        (field) =>
          field !== GRID_CHECKBOX_SELECTION_COL_DEF.field &&
          !fixedWidthFields?.includes(field),
      ),
    [autosizeOptions.columns, columns, fixedWidthFields],
  );
  const effectiveAutosizeOptions = useMemo(
    () => ({
      ...autosizeOptions,
      columns: autosizeEligibleColumns,
    }),
    [autosizeOptions, autosizeEligibleColumns],
  );

  // Re-arm autosizing when the sizing options change, or when the `columns`
  // identity changes — DataGridPro then rebuilds column state at colDef
  // widths (see the memo above), leaving never-user-resized columns at their
  // defaults until the next autosize pass.
  useEffect(() => {
    hasAutosizedRef.current = false;
  }, [autosizeOptions, columns]);

  useEffect(() => {
    if (!loading && rows.length && !TEST_RUN && !hasAutosizedRef.current) {
      // Even with the useEffect, there can be a small race between the
      // rendering and autosizing leading to column sizes that are just
      // slightly off. See https://stackoverflow.com/a/78411672
      setTimeout(
        () => apiRef.current?.autosizeColumns(effectiveAutosizeOptions),
        0,
      );
      hasAutosizedRef.current = true;
    }
    // rows.length (not rows) — avoids re-autosizing on data-only refreshes
    // with the same row count; hasAutosizedRef resets on autosizeOptions or
    // columns change (effectiveAutosizeOptions re-fires this effect then).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, rows.length, apiRef, effectiveAutosizeOptions]);

  const mergedSlotProps = {
    ...slotPropsFromParent,
    footer: {
      totalRows,
      checkboxSelection,
      selectedRowsAmount: Array.isArray(rowSelectionModel)
        ? rowSelectionModel.length
        : rowSelectionModel !== undefined && rowSelectionModel !== null
          ? 1
          : 0,
      ...slotPropsFromParent?.footer,
    },
    panel: {
      placement: "bottom-end" as const,
      ...slotPropsFromParent?.panel,
    },
    toolbar: {
      exportInProgress,
      handleExport,
      customInline,
      enableFilterButton,
      hideDensityButton,
      ...slotPropsFromParent?.toolbar,
    },
  };

  // Don't autosize specific columns if user has manually set their size
  function onColumnResize(params: GridColumnResizeParams) {
    const resizedField = params.colDef.field;

    userResizedWidthsRef.current[resizedField] = params.width;

    setAutosizeOptions((prev) => {
      const currentEligible =
        prev.columns && prev.columns.length
          ? prev.columns
          : columns.map((col) => col.field);

      const nextEligible = currentEligible.filter(
        (field) => field !== resizedField,
      );

      return {
        ...prev,
        columns: nextEligible,
      };
    });
  }

  const mergedDataGridClassName = parentDataGridClassName
    ? `${BASE_GRID_CLASSES} ${parentDataGridClassName}`
    : BASE_GRID_CLASSES;

  const dataGridStyles = {
    "--xgrid-row-border-color": "transparent",
    "--xgrid-toolbar-button-color": COLORS.orange.base,
    ...parentDataGridStyle,
  } as React.CSSProperties;

  return (
    <div
      className="flex"
      style={{
        height:
          totalRows < maxAutoHeightRows
            ? "100%"
            : customHeight || `calc(100vh - 180px)`,
        minHeight:
          totalRows < maxAutoHeightRows ? `${totalRows * 10}px` : "30vh",
      }}
    >
      <div className="grid h-full w-full">
        <DataGridPro
          apiRef={apiRef}
          autoHeight={totalRows < maxAutoHeightRows}
          autosizeOnMount={!TEST_RUN}
          autosizeOptions={effectiveAutosizeOptions}
          {...rest}
          checkboxSelection={checkboxSelection}
          className={mergedDataGridClassName}
          columns={columns}
          density={tableDensity}
          disableVirtualization={TEST_RUN || disableVirtualization}
          getRowClassName={
            getRowClassName ??
            ((params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd")
          }
          initialState={
            tableState
              ? stripPinnedColumnsInTest({
                  ...(checkboxSelection
                    ? pinCheckboxColumnToLeft(tableState)
                    : tableState),
                  ...initialState,
                })
              : undefined
          }
          loading={loading}
          localeText={{ toolbarColumns: "", toolbarFilters: "" }} // remove labels from filter and column buttons
          onColumnHeaderClick={onColumnHeaderClick}
          onColumnResize={onColumnResize}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          onStateChange={() => {
            if (apiRef?.current?.exportState) {
              setTableState(apiRef.current.exportState());
            }
            const currentDensity = apiRef?.current
              ? gridDensityValueSelector(apiRef.current.state)
              : undefined;
            if (currentDensity && currentDensity !== tableDensity) {
              setTableDensity(currentDensity);
            }
          }}
          rowHeight={rowHeight}
          rowSelectionModel={rowSelectionModel}
          rows={rows}
          slotProps={mergedSlotProps}
          slots={{
            footer: CustomFooter,
            loadingOverlay: CustomLoadingOverlay,
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: hasToolbar ? CustomToolbar : null,
            ...rest.slots,
          }}
          style={dataGridStyles}
          disableColumnReorder
          hideFooterPagination
          hideFooterRowCount
        />
      </div>
    </div>
  );
}

// Pinned columns render duplicate row DOM under jsdom (virtualization is off in
// tests), which breaks role-based queries like getAllByRole("row"). Strip
// pinned columns during test runs so pinning stays a visual-only concern.
function stripPinnedColumnsInTest(
  state: GridInitialStatePro,
): GridInitialStatePro {
  if (!TEST_RUN || !state.pinnedColumns) {
    return state;
  }

  const { pinnedColumns: _pinnedColumns, ...rest } = state;
  return rest;
}

function pinCheckboxColumnToLeft(
  state: GridInitialStatePro,
): GridInitialStatePro {
  // Disable auto pinning during test runs to avoid duplicate DOM elements getting by getAllByRole
  // and making the tests messy
  if (TEST_RUN) {
    return state;
  }

  const leftPinnedColumns = state.pinnedColumns?.left || [];

  if (!leftPinnedColumns.includes(GRID_CHECKBOX_SELECTION_COL_DEF.field)) {
    return {
      ...state,
      pinnedColumns: {
        ...state.pinnedColumns,
        left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, ...leftPinnedColumns],
      },
    };
  }

  return state;
}
